import React, { useState } from "react";
import { AdvancedFilters } from "@/components/ui";
import { FilterBar } from "@/components/ui";
import { SearchBar, FormSelect, Button } from "@/components/ui";
import { Search, X, Plus, Calendar, Filter, RotateCcw } from "lucide-react";
import { fmtDate } from "@/lib/formatters";
import DateFilter from "@/components/ui/DateFilter";

/**
 * InventoryControlPanel Component
 * Refactored to use the standard FilterSection pattern from the new-frontend design system.
 */
export const InventoryControlPanel = ({
  activeTab,
  search, setSearch,
  searchPlaceholder,
  filters, setFilters,
  dynamicSettings,
  onAddItemClick,
  buttonLabel = "New Item",
  onBackClick,
  showBack = false,
  categories: customCategories,
  parties,
  projects,
  partyLabel = "Vendors",
  showStatusFilter = true,
  customStatusOptions = null,
  statusPlaceholder = "All Statuses",
  showVendorFilter = true,
  showCustomerFilter = true,
  showCategoryFilter = true,
  showItemFilter = false,
  masterItems = [],
  onExport,
  isExporting,
  children
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleFilterChange = (key, value) => {
    if (typeof key === 'object') {
      setFilters(prev => ({ ...prev, ...key }));
    } else {
      setFilters(prev => ({ ...prev, [key]: value }));
    }
  };

  // Status options for the standard FilterSection
  const statusOptions = showStatusFilter ? (customStatusOptions || [
    { value: 'in', label: 'In Stock' },
    { value: 'low', label: 'Low Stock' },
    { value: 'out', label: 'Out of Stock' }
  ]) : [];

  const customFilters = [];

  if (showStatusFilter) {
    customFilters.push({
      id: 'status',
      label: statusPlaceholder,
      value: filters.status || 'all',
      onChange: (val) => handleFilterChange('status', val),
      options: [{ value: 'all', label: statusPlaceholder }, ...statusOptions]
    });
  }

  /* 
  if (showCategoryFilter !== false) {
     const options = (customCategories || dynamicSettings?.cats || []).map(c => {
        const label = c.categoryName || c.title || c.name || (typeof c === 'string' ? c : "");
        const safeLabel = typeof label === 'object' ? "—" : (label || "—");
        return { value: c._id || String(safeLabel), label: String(safeLabel) };
     });
     customFilters.push({
       id: 'category',
       label: "All Categories",
       value: filters.category || 'all',
       onChange: (val) => handleFilterChange('category', val),
       options: [{ value: 'all', label: 'All Categories' }, ...options]
     });
  }

  if (showVendorFilter !== false) {
     const vList = parties ? parties.filter(p => p.type === 'vendor' || !p.type) : (dynamicSettings?.suppliers || []);
     const options = vList.map(p => {
        const label = p.vendorName || p.name || p.title || (typeof p === 'string' ? p : "");
        const safeLabel = typeof label === 'object' ? "—" : (label || "—");
        const value = p.value || p._id || String(safeLabel);
        return { value, label: String(label) };
     });
     customFilters.push({
       id: 'vendor',
       label: "All Vendors",
       value: filters.supplier || 'all',
       onChange: (val) => handleFilterChange('supplier', val),
       options: [{ value: 'all', label: 'All Vendors' }, ...options]
     });
  }
  */

  if (showCustomerFilter !== false) {
     const rawList = parties ? parties.filter(p => p.type === 'customer') : (projects || dynamicSettings?.projectsLocal || dynamicSettings?.projects || []);
     const uniqueCustomers = [];
     const seenNames = new Set();
     rawList.forEach(p => {
       const label = p.customerName || p.name || p.title || (typeof p === 'string' ? p : "");
       const safeLabel = typeof label === 'object' ? "—" : (label || "—");
       const nameKey = String(safeLabel).toLowerCase().trim();
       if (nameKey && nameKey !== "—" && !seenNames.has(nameKey)) {
         seenNames.add(nameKey);
         uniqueCustomers.push({ label: String(safeLabel), value: String(safeLabel) });
       }
     });
     customFilters.push({
       id: 'customer',
       label: "All Projects/Customers",
       value: filters.customer || 'all',
       onChange: (val) => handleFilterChange('customer', val),
       options: [{ value: 'all', label: 'All Projects/Customers' }, ...uniqueCustomers]
     });
  }

  return (
    <>
      {showBack && (
        <div className="mb-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={onBackClick}
            className="h-9 px-4 text-[10px] font-black uppercase tracking-widest"
            leftIcon={<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path d="M15 19l-7-7 7-7" /></svg>}
          >
            BACK
          </Button>
        </div>
      )}
      <FilterBar
        searchPlaceholder={searchPlaceholder}
        searchTerm={search}
        setSearchTerm={setSearch}
        filters={customFilters}
        showDateRange={true}
        showTimeRangeToggles={true}
        fromDate={filters.fromDate || ''}
        toDate={filters.toDate || ''}
        activePreset={filters.date !== 'all' ? filters.date : ''}
        onDateChange={(field, value) => handleFilterChange(field, value)}
        onDatePreset={(preset) => handleFilterChange({ date: preset, fromDate: '', toDate: '' })}
        onExport={onExport}
        isExporting={isExporting}
        onAddClick={onAddItemClick}
        addLabel={buttonLabel}
        onReset={() => handleFilterChange({
          category: 'all',
          item: 'all',
          supplier: 'all',
          customer: 'all',
          status: 'all',
          date: 'all',
          fromDate: '',
          toDate: ''
        })}
      />
      {children}
    </>
  );
};